import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaCheckCircle, FaRegClock, FaPercent, FaShieldAlt, FaDollarSign, FaCalculator } from 'react-icons/fa';

const Promotions = () => {
  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const currentPromotions = [
    {
      title: "Spring Special: 75% Off Installation",
      description: "Get 75% off professional installation with any whole-house window replacement",
      expiry: "April 30th, 2024",
      features: [
        "Professional installation",
        "Complimentary hurricane shutters",
        "Energy efficiency upgrade",
        "Extended warranty"
      ]
    },
    {
      title: "Military & First Responder Discount",
      description: "Additional 10% off for all active duty military, veterans, and first responders",
      expiry: "No expiration",
      features: [
        "Valid military/service ID required",
        "Combinable with other offers",
        "Includes family members",
        "Priority scheduling"
      ]
    },
    {
      title: "Energy Efficiency Package",
      description: "Free UV protection upgrade with any impact window purchase",
      expiry: "Limited time offer",
      features: [
        "Enhanced UV protection",
        "Improved energy efficiency",
        "Reduced cooling costs",
        "Furniture protection"
      ]
    }
  ];

  const testimonials = [
    {
      name: "Sarah Johnson",
      location: "Miami, FL",
      quote: "We saved over $2,000 on our window installation with the spring special. The service was excellent!",
      rating: 5
    },
    {
      name: "Michael Rodriguez",
      location: "Tampa, FL",
      quote: "As a veteran, I appreciate the military discount. The team was professional and the windows are fantastic.",
      rating: 5
    },
    {
      name: "Emily Chen",
      location: "Orlando, FL",
      quote: "The energy efficiency package was worth every penny. Our AC bills have dropped significantly!",
      rating: 5
    }
  ];

  const terms = [
    "Offers cannot be combined unless specifically stated",
    "All promotions are subject to change without notice",
    "Installation discounts apply to standard installation only",
    "Military/First Responder discounts require valid identification",
    "Energy efficiency claims based on average customer savings",
    "Some restrictions may apply to financing offers"
  ];

  const [selectedProject, setSelectedProject] = useState('windows');
  
  const projectTypes = {
    windows: {
      name: 'Window Replacement',
      basePrice: 15000,
      savings: [
        {
          title: '75% Off Installation',
          amount: 2250,
          description: 'Spring installation discount'
        },
        {
          title: 'Energy Efficiency Package',
          amount: 1000,
          description: 'Free UV protection upgrade'
        },
        {
          title: 'Energy Bill Savings',
          amount: 800,
          description: 'Estimated annual savings'
        }
      ]
    },
    doors: {
      name: 'Impact Doors',
      basePrice: 8000,
      savings: [
        {
          title: '75% Off Installation',
          amount: 1500,
          description: 'Spring installation discount'
        },
        {
          title: 'Energy Efficiency Package',
          amount: 750,
          description: 'Free UV protection upgrade'
        },
        {
          title: 'Energy Bill Savings',
          amount: 400,
          description: 'Estimated annual savings'
        }
      ]
    }
  };

  const calculateTotalSavings = (project) => {
    return project.savings.reduce((total, saving) => total + saving.amount, 0);
  };

  return (
    <div className="pt-28 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <motion.div
          initial={fadeIn.initial}
          animate={fadeIn.animate}
          transition={fadeIn.transition}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-1 bg-primary-100 text-primary-600 rounded-full text-sm font-medium mb-4">LIMITED TIME OFFERS</span>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Current Promotions & Special Offers
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Take advantage of our limited-time offers and save on your home improvement project.
            Don't miss out on these exclusive deals!
          </p>
        </motion.div>

        {/* Promotions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {currentPromotions.map((promo, index) => (
            <motion.div
              key={promo.title}
              initial={fadeIn.initial}
              whileInView={fadeIn.animate}
              viewport={{ once: true }}
              transition={{ ...fadeIn.transition, delay: index * 0.2 }}
              className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300"
            >
              <div className="bg-primary-600 text-white p-6">
                <h3 className="text-2xl font-bold mb-2">{promo.title}</h3>
                <p className="text-sm flex items-center">
                  <FaRegClock className="mr-2" />
                  Expires: {promo.expiry}
                </p>
              </div>
              
              <div className="p-6">
                <p className="text-gray-600 mb-6">
                  {promo.description}
                </p>
                
                <h4 className="font-semibold text-gray-900 mb-3">Includes:</h4>
                <ul className="space-y-2">
                  {promo.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-600">
                      <FaCheckCircle className="w-5 h-5 text-green-500 mr-3" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="p-6 bg-gray-50 border-t border-gray-100">
                <Link
                  to="/get-estimate"
                  className="block w-full bg-primary-600 text-white text-center px-6 py-3 rounded-md hover:bg-primary-700 transition duration-300"
                >
                  Claim Offer
                </Link>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Savings Breakdown Section */}
        <motion.div
          initial={fadeIn.initial}
          whileInView={fadeIn.animate}
          viewport={{ once: true }}
          transition={fadeIn.transition}
          className="bg-gray-50 rounded-lg p-12 mb-16"
        >
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Calculate Your Savings
            </h2>
            <p className="text-gray-600">
              See how much you can save with our current promotions and energy-efficient products
            </p>
          </div>

          <div className="flex flex-col lg:flex-row items-start gap-8">
            <div className="w-full lg:w-1/3">
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Select Your Project</h3>
                <div className="space-y-3">
                  <button
                    onClick={() => setSelectedProject('windows')}
                    className={`w-full px-4 py-3 rounded-md text-left transition duration-200 ${
                      selectedProject === 'windows'
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Whole House Windows
                  </button>
                  <button
                    onClick={() => setSelectedProject('doors')}
                    className={`w-full px-4 py-3 rounded-md text-left transition duration-200 ${
                      selectedProject === 'doors'
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Impact Doors
                  </button>
                </div>
              </div>
            </div>

            <div className="w-full lg:w-2/3">
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-6">
                  Potential Savings for {projectTypes[selectedProject].name}
                </h3>
                <div className="space-y-4">
                  {projectTypes[selectedProject].savings.map((saving, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center">
                        <FaDollarSign className="w-6 h-6 text-green-500 mr-3" />
                        <div>
                          <p className="font-semibold text-gray-900">{saving.title}</p>
                          <p className="text-sm text-gray-600">{saving.description}</p>
                        </div>
                      </div>
                      <p className="text-xl font-bold text-green-600">
                        ${saving.amount.toLocaleString()}
                      </p>
                    </div>
                  ))}
                  <div className="flex items-center justify-between p-4 bg-primary-50 rounded-lg border-2 border-primary-100">
                    <div>
                      <p className="font-bold text-gray-900">Total Potential Savings</p>
                      <p className="text-sm text-gray-600">Combined savings and incentives</p>
                    </div>
                    <p className="text-2xl font-bold text-primary-600">
                      ${calculateTotalSavings(projectTypes[selectedProject]).toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="mt-6 text-center">
                  <Link
                    to="/get-estimate"
                    className="inline-flex items-center justify-center bg-primary-600 text-white px-6 py-3 rounded-md hover:bg-primary-700 transition duration-300"
                  >
                    <FaCalculator className="mr-2" />
                    Get Detailed Quote
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Testimonials */}
        <motion.div
          initial={fadeIn.initial}
          whileInView={fadeIn.animate}
          viewport={{ once: true }}
          transition={fadeIn.transition}
          className="mb-16"
        >
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Customer Success Stories
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-gray-600 mb-4 italic">"{testimonial.quote}"</p>
                <div className="text-sm">
                  <p className="font-semibold text-gray-900">{testimonial.name}</p>
                  <p className="text-gray-500">{testimonial.location}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Terms and Conditions */}
        <motion.div
          initial={fadeIn.initial}
          whileInView={fadeIn.animate}
          viewport={{ once: true }}
          transition={fadeIn.transition}
          className="bg-white rounded-lg shadow-md p-8 mb-16"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Terms & Conditions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {terms.map((term, index) => (
              <div key={index} className="flex items-start">
                <FaCheckCircle className="w-5 h-5 text-primary-600 mr-3 mt-1" />
                <p className="text-gray-600">{term}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Call to Action */}
        <motion.div
          initial={fadeIn.initial}
          whileInView={fadeIn.animate}
          viewport={{ once: true }}
          transition={fadeIn.transition}
          className="text-center bg-primary-600 rounded-lg p-12 text-white"
        >
          <h2 className="text-3xl font-bold mb-4">
            Don't Miss Out on These Savings!
          </h2>
          <p className="text-xl mb-8">
            Contact us today to take advantage of these limited-time offers.
            Our experts are ready to help you transform your home.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/get-estimate"
              className="bg-white text-primary-600 px-8 py-3 rounded-md text-lg font-semibold hover:bg-gray-100 transition duration-300"
            >
              Get Your Free Quote
            </Link>
            <Link
              to="/get-estimate"
              className="border-2 border-white text-white px-8 py-3 rounded-md text-lg font-semibold hover:bg-white hover:text-primary-600 transition duration-300"
            >
              Schedule Consultation
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Promotions; 